import BaseButton from "@components/ui/BaseButton";
import BaseCheckbox from "@components/ui/BaseCheckbox";
import BaseContainer from "@components/ui/BaseContainer";
import BaseInput from "@components/ui/BaseInput";
import { notifyError } from "@utils/helpers/notify";
import { validatePhoneNumber } from "@utils/helpers/validations";
import clsx from "clsx";
import { ChangeEvent, useReducer, FormEvent, useState } from "react";
import { AnimationOnScroll } from "react-animation-on-scroll";
import { useTranslation } from "react-i18next";
import classes from "./Request.module.scss";
import { callMeBackConversion } from "../../utils/gtag/gtag";
import Popup from "@components/ui/Popup";

type ActionType = {
  type: string;
  payload?: {
    field?: keyof typeof initialState;
    value?: string | boolean;
  };
};

const initialState = {
  name: "",
  phone: "",
  checkbox: false,
};

const reducer = (state = initialState, action: ActionType) => {
  const payload = action.payload!;

  switch (action.type) {
    case "input": {
      return {
        ...state,
        [payload.field!]: payload.value,
      };
    }

    case "checkbox": {
      return {
        ...state,
        checkbox: !state.checkbox,
      };
    }

    case "reset": {
      return initialState;
    }

    default:
      return state;
  }
};

const Request = () => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, initialState);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange =
    (field: keyof typeof initialState) =>
    (e: ChangeEvent<HTMLInputElement>) => {
      setIsSubmitted(false);

      dispatch({
        type: "input",
        payload: {
          field,
          value: e.target.value,
        },
      });
    };

  const handleCheckboxChange = () => {
    setIsSubmitted(false);
    dispatch({ type: "checkbox" });
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitted(true);

    const isInvalid = Object.values(state).some((value) => !value);

    if (isInvalid) return;

    const data = {
      name: state.name,
      phone: state.phone,
    };

    fetch("https://feedbacker.lavina.tech/2", {
      method: "post",
      body: JSON.stringify(data),
    })
      .then(() => {
        dispatch({ type: "reset" });
        setIsSubmitted(false);
        setIsSuccess(true);
        callMeBackConversion();
      })
      .catch(notifyError);
  };

  return (
    <>
      <div className={classes.request} id="contacts">
        <BaseContainer className={classes.request__container}>
          <AnimationOnScroll animateIn="animate__fadeInUp" animateOnce>
            <h2 className={classes.request__title}>{t("request.title")}</h2>
          </AnimationOnScroll>
          <form
            className={clsx(classes.request__form, classes.form)}
            onSubmit={handleSubmit}
          >
            <AnimationOnScroll animateIn="animate__fadeInUp" animateOnce>
              <p className={classes.form__subtitle}>{t("request.subtitle")}</p>
            </AnimationOnScroll>
            <BaseInput
              className={clsx(classes.form__input, {
                [classes.error]: isSubmitted && state.name.length < 3,
              })}
              value={state.name}
              onChange={handleChange("name")}
              label={t("request.form.name")}
            />
            <BaseInput
              className={clsx(classes.form__input, {
                [classes.error]:
                  isSubmitted && !validatePhoneNumber(state.phone),
              })}
              value={state.phone}
              onChange={handleChange("phone")}
              label={t("request.form.phone")}
            />

            <BaseCheckbox
              className={clsx(classes.form__checkbox, classes.checkbox, {
                [classes.error]: isSubmitted && !state.checkbox,
              })}
              label={
                <span>
                  {t("request.form.checkboxText")}{" "}
                  {t("request.form.checkboxLink")}
                </span>
              }
              checked={state.checkbox}
              onChange={handleCheckboxChange}
            />

            <BaseButton className={classes.form__button} type="submit">
              {t("request.form.buttonText")}
            </BaseButton>
          </form>
        </BaseContainer>
      </div>
      {isSuccess ? <Popup onClose={() => setIsSuccess(false)} /> : null}
    </>
  );
};

export default Request;
